// التقرير — صفحة واحدة تُقرأ من أعلى لأسفل، لا قائمة تقارير.
//
// المستخدم لا يملك سؤالاً مصاغاً بل قلقاً؛ اعرض عليه قائمة تقارير فيختار عشوائياً
// ثم يخرج. اختيار الفترة هو الأداة الوحيدة، وما عداه أقسام مرتّبة من الرقم الكبير
// إلى التفصيل. وكل بند قابل للضغط لرؤية حركاته — بدونه يصبح كل رقم طريقاً مسدوداً.

import { fmt, esc, ymd, fromYmd, shortDate, monthStart, startOfDay } from '../util.js';
import { baseSym, destName, typeName, netTotal } from '../store.js';
import { PERIODS, range, spend, income, count, groupBy, movers, buckets,
         fixedVsVariable, biggest, crossTable, projection, entriesIn } from '../report.js';
import { topBar, backBar, bar, deltaTag, entryRow, emptyState, loadingBlock, offlineBlock, errorBlock } from '../ui.js';

export function freshState(){
  return { period: 'month', from: null, to: null, by: 'type', drill: null };
}

/**
 * الفترة التي يحتاجها التقرير: المعروضة **ومعها فترة المقارنة**.
 * بدون فترة المقارنة تظهر كل النِسب «جديد» لأن السابقة تُقرأ صفراً.
 */
export function rangeFor(app){
  const r = range(app.rep.period, app.rep.from, app.rep.to);
  return { from: Math.min(r.f, r.pf ?? r.f), to: Math.max(r.t, r.pt ?? r.t) };
}

export function render(app){
  const label = (() => { try { return range(app.rep.period, app.rep.from, app.rep.to).label; }
                         catch { return ''; } })();
  if (app.loading)              return topBar('التقرير', esc(label)) + loadingBlock('حركات الفترة');
  if (app.dataErr === 'offline') return topBar('التقرير', esc(label)) + offlineBlock('التقارير');
  if (app.dataErr)              return topBar('التقرير', esc(label)) + errorBlock(app.dataErr);

  if (app.rep.drill) return renderDrill(app);

  const r = range(app.rep.period, app.rep.from, app.rep.to);
  const sym = baseSym();
  const total = spend(r.f, r.t);
  const prevTotal = spend(r.pf, r.pt);
  const inc = income(r.f, r.t);
  const n = count(r.f, r.t, e => e.kind !== 'in');

  const chips = `<div class="pchips noscroll">${PERIODS.map(([id, label]) =>
      `<button type="button" class="chip" data-period="${id}" aria-pressed="${app.rep.period === id}">${label}</button>`
    ).join('')}</div>`;

  const dates = app.rep.period === 'custom'
    ? `<div class="dates">
        <label>من<input type="date" id="repFrom" value="${ymd(app.rep.from || monthStart(Date.now(), 0))}"></label>
        <label>إلى<input type="date" id="repTo" value="${ymd(app.rep.to || startOfDay(Date.now()))}"></label>
      </div>`
    : '';

  const seg = `<div class="seg">
      ${[['type', 'حسب النوع'], ['dest', 'حسب الوجهة'], ['cross', 'التقاطع']].map(([id, label]) =>
        `<button type="button" data-by="${id}" aria-pressed="${app.rep.by === id}">${label}</button>`).join('')}
    </div>`;

  if (total === 0 && inc === 0){
    return topBar('التقرير', esc(r.label)) + chips + dates + seg
      + `<div class="scroll">${emptyState('لا توجد حركات في هذه الفترة',
          'اختر فترة أخرى، أو سجّل حركات جديدة من شاشة التسجيل.')}</div>`;
  }

  const { rows, untyped } = groupBy(r.f, r.t, app.rep.by === 'dest' ? 'dest' : 'type');
  const pctNone = total ? Math.round(untyped / total * 100) : 0;

  // زرّ لا لافتة: التنبيه الذي يذكر مشكلة دون أن يوصل إليها يترك المستخدم
  // يبحث عن الشاشة بنفسه. الضغط عليه يفتح كومة هذه الفترة وحدها.
  const warn = (app.rep.by !== 'dest' && untyped && pctNone >= 10)
    ? `<button type="button" class="warnbar" data-act="fixuntyped">
        <span>⚠</span>
        <span class="wtxt"><b>${pctNone}٪ من مصروف هذه الفترة بلا نوع</b>
          (${fmt(untyped)} ${sym}). التقرير حسب النوع ناقص بهذا القدر.
          <b class="wcta">صنّفها الآن</b></span>
        <span class="wgo" aria-hidden="true">‹</span>
      </button>`
    : '';

  return topBar('التقرير', esc(r.label)) + chips + dates + seg
    + `<div class="scroll">${warn}
        ${sectionSummary(app, r, total, prevTotal, inc, n, sym)}
        ${sectionTrend(r, sym)}
        ${sectionBreakdown(app, r, rows, total)}
        ${sectionMovers(app, r, sym)}
        ${sectionFixed(r, sym)}
        ${sectionBiggest(r)}
      </div>`;
}

/* ------------------------------- الأقسام ------------------------------- */

function sectionSummary(app, r, total, prevTotal, inc, n, sym){
  const avg = total / r.days;
  const isMonth = app.rep.period === 'month';
  const proj = isMonth ? projection(r.f, r.days) : 0;
  const left = inc - total;
  // الصافي الكلّيّ من العدّادات لا من حركاتٍ تُجلب: التاريخ ليس على الجهاز،
  // وجلبُه كاملاً لأجل رقمٍ واحدٍ يناقض المعمارية
  const allNet = netTotal('all');

  return `<div class="sec"><h5>الملخّص</h5><div class="stats">
      <div class="stat wide big"><div class="k">إجمالي المصروف</div>
        <div class="v">${fmt(total)}<small>${sym}</small> ${deltaTag(total, prevTotal)}</div></div>
      <div class="stat"><div class="k">المتوسط اليومي</div><div class="v">${fmt(avg)}<small>${sym}</small></div></div>
      <div class="stat"><div class="k">عدد الحركات</div><div class="v">${n}</div></div>
      <div class="stat wide"><div class="k">${esc(r.pLabel)}</div>
        <div class="v">${fmt(prevTotal)}<small>${sym}</small></div></div>
      <div class="stat${inc > 0 ? ' pos' : ''}"><div class="k">الدخل</div>
        <div class="v">${fmt(inc)}<small>${sym}</small></div></div>
      <div class="stat${left >= 0 ? ' pos' : ' neg'}"><div class="k">الصافي</div>
        <div class="v">${left < 0 ? '‎− ' : ''}${fmt(Math.abs(left))}<small>${sym}</small></div></div>
      <div class="stat wide${allNet >= 0 ? ' pos' : ' neg'}"><div class="k">الصافي الكلي — كل الحركات</div>
        <div class="v">${allNet < 0 ? '‎− ' : ''}${fmt(Math.abs(allNet))}<small>${sym}</small></div></div>
      ${isMonth ? `<div class="stat wide"><div class="k">بهذا المعدّل ستنهي الشهر عند</div>
        <div class="v">${fmt(proj)}<small>${sym}</small></div></div>` : ''}
    </div></div>`;
}

function sectionTrend(r, sym){
  const b = buckets(r.f, r.t);
  if (b.list.length < 3) return '';
  return `<div class="sec"><h5>الاتجاه · ${b.unit}</h5>
      <div class="trend">${b.list.map(x =>
        `<i class="${x.value === b.max && b.max > 0 ? 'hi' : ''}" style="height:${
          Math.max(2, b.max ? x.value / b.max * 100 : 0)}%"></i>`).join('')}</div>
      <div class="tlegend">
        <span>${esc(shortDate(b.list[0].from))}</span>
        <span>الأعلى: ${fmt(b.max)} ${sym}</span>
        <span>${esc(shortDate(b.list[b.list.length - 1].from))}</span>
      </div></div>`;
}

function sectionBreakdown(app, r, rows, total){
  if (app.rep.by === 'cross'){
    const c = crossTable(r.f, r.t);
    if (!c.typeRows.length) return '';
    const head = '<tr><th class="rh"></th>' +
      c.destRows.map(d => `<th>${esc(d.name.split(' ')[0])}</th>`).join('') + '</tr>';
    const body = c.typeRows.map(t => '<tr><th class="rh">' + esc(t.name) + '</th>' +
      c.destRows.map(d => {
        const v = c.cells.get(`${t.id}|${d.id}`) || 0;
        const a = c.max ? v / c.max : 0;
        const bg = v ? `color-mix(in srgb, var(--dest) ${Math.round(12 + a * 68)}%, var(--sunk))` : 'var(--sunk)';
        const fg = a > 0.45 ? 'var(--dest-ink)' : (v ? 'var(--ink)' : 'var(--line-2)');
        return `<td style="background:${bg};color:${fg}">${v ? fmt(v) : '·'}</td>`;
      }).join('') + '</tr>').join('');
    return `<div class="sec"><h5>تقاطع البُعدين</h5>
      <div class="mxwrap"><table class="mx">${head}${body}</table></div>
      <p class="note-sm">كم أنفقت على كل نوع ولكل وجهة. <b>هذا الجدول ناتج عن نقرتين لكل حركة،
      ولا يمكن لأي تصنيف شجري أن ينتجه.</b></p></div>`;
  }

  const key = app.rep.by === 'dest' ? 'dest' : 'type';
  const color = key === 'dest' ? 'var(--dest)' : 'var(--type)';
  const max = rows.length ? rows[0].value : 1;
  return `<div class="sec"><h5>${key === 'dest' ? 'على من ذهب المال' : 'على ماذا صُرف المال'}</h5>
      ${rows.map(x => bar(x.name, x.value, max, total,
        x.none ? 'var(--line-2)' : color, `data-drill="${key}|${x.id}"`)).join('')}
      <p class="note-sm">اضغط أي بند لرؤية الحركات التي كوّنته.</p></div>`;
}

function sectionMovers(app, r, sym){
  const key = app.rep.by === 'dest' ? 'dest' : 'type';
  const m = movers(r.f, r.t, r.pf, r.pt, key);
  const list = m.up.concat(m.down);
  if (!list.length) return '';
  return `<div class="sec"><h5>المقارنة مع ${esc(r.pLabel)}</h5>
      ${list.map(x => `<button type="button" class="cmp" data-drill="${key}|${x.id}">
        <span class="nm">${esc(x.name)}<small>${fmt(x.was)} ← ${fmt(x.now)} ${sym}</small></span>
        <span class="d"><span class="a" style="color:${x.delta > 0 ? 'var(--up)' : 'var(--dest)'}">
          ${x.delta > 0 ? '+' : '−'}${fmt(Math.abs(x.delta))}</span>${deltaTag(x.now, x.was)}</span>
      </button>`).join('')}
      <p class="note-sm">المقارنة على <b>أيام متساوية</b> — لا يُقارَن نصف شهر بشهر كامل.</p></div>`;
}

function sectionFixed(r, sym){
  const fv = fixedVsVariable(r.f, r.t);
  if (!fv.total) return '';
  const pct = Math.round(fv.variable / fv.total * 100);
  return `<div class="sec"><h5>ثابت ومتغيّر</h5>
      <div class="fvbar">
        <i style="width:${fv.fixed / fv.total * 100}%;background:var(--muted)"></i>
        <i style="width:${fv.variable / fv.total * 100}%;background:var(--dest)"></i>
      </div>
      <div class="fvkey">
        <span><b style="background:var(--muted)"></b>ثابت ${fmt(fv.fixed)} ${sym}</span>
        <span><b style="background:var(--dest)"></b>متغيّر ${fmt(fv.variable)} ${sym}</span>
      </div>
      <p class="note-sm">المتغيّر وحده هو ما يمكنك التحكم به فعلاً، وهو <b>${pct}٪</b> من مصروفك.
      أي هدف توفير يجب أن يُحسب على هذا الرقم لا على الإجمالي — وإلا كان هدفاً مستحيلاً.
      حدّد ما هو ثابت من شاشة الإعدادات.</p></div>`;
}

function sectionBiggest(r){
  const list = biggest(r.f, r.t);
  if (!list.length) return '';
  return `<div class="sec"><h5>أكبر الحركات المفردة</h5>
      ${list.map(e => entryRow(e, { showDate: true })).join('')}</div>`;
}

/* ------------------------------- التفصيل ------------------------------- */

function renderDrill(app){
  const r = range(app.rep.period, app.rep.from, app.rep.to);
  const { kind, id } = app.rep.drill;
  const sym = baseSym();
  const list = entriesIn(r.f, r.t, e => {
    if (e.kind === 'in') return false;
    if (kind === 'dest') return e.destId === id;
    return (e.typeId || '__none__') === id;
  });
  const total = list.reduce((s, e) => s + e.amount, 0);
  const name = id === '__none__' ? 'بلا نوع' : (kind === 'dest' ? destName(id) : (typeName(id) || 'بلا نوع'));

  return backBar(name, `${esc(r.label)} · ${list.length} حركة · ${fmt(total)} ${sym}`)
    + `<div class="scroll">${list.map(e => entryRow(e, { showDate: true })).join('')}</div>`;
}

/* ------------------------------- الأحداث ------------------------------- */

export function mount(app, root){
  const f = root.querySelector('#repFrom');
  const t = root.querySelector('#repTo');
  if (f) f.addEventListener('change', () => {
    const v = fromYmd(f.value); if (v != null){ app.rep.from = v; app.render(); app.ensureData(); }
  });
  if (t) t.addEventListener('change', () => {
    const v = fromYmd(t.value); if (v != null){ app.rep.to = v; app.render(); app.ensureData(); }
  });
}

export function onClick(app, btn){
  if (btn.dataset.period){
    app.rep.period = btn.dataset.period;
    app.rep.drill = null;
    if (app.rep.period === 'custom'){
      if (app.rep.from == null) app.rep.from = monthStart(Date.now(), 0);
      if (app.rep.to == null) app.rep.to = startOfDay(Date.now());
    }
    app.render();
    return app.ensureData();          // الفترة الجديدة قد تكون خارج المحمّل
  }
  if (btn.dataset.by){ app.rep.by = btn.dataset.by; app.rep.drill = null; return app.render(); }
  if (btn.dataset.drill){
    const [kind, id] = btn.dataset.drill.split('|');
    app.rep.drill = { kind, id };
    return app.render();
  }
  if (btn.dataset.entry){ app.openEdit(btn.dataset.entry); return; }
  if (btn.dataset.act === 'fixuntyped'){
    const r = range(app.rep.period, app.rep.from, app.rep.to);
    return app.openSub('classify', { range: { f: r.f, t: r.t }, label: r.label });
  }
  if (btn.dataset.act === 'back'){ app.rep.drill = null; return app.render(); }
  return false;
}
